<template>
  <div ref="root" class="user_menu">
    <div
      class="user_menu__trigger"
      :class="{ 'user_menu__trigger--open': isOpen }"
      role="button"
      tabindex="0"
      aria-haspopup="menu"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <div class="user_menu__info">
        <p class="user_menu__username">{{ username }}</p>
        <div class="user_menu__coin">
          <svg
            class="user_menu__coin_icon"
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5 10C7.76142 10 10 7.76142 10 5C10 2.23858 7.76142 0 5 0C2.23858 0 0 2.23858 0 5C0 7.76142 2.23858 10 5 10Z"
              fill="#FFC124"
            />
            <path
              d="M4.30958 4.73479C4.30958 4.46134 4.34612 4.23032 4.4192 4.04173C4.49228 3.85314 4.59718 3.70934 4.73391 3.61033C4.87064 3.50896 5.03566 3.45827 5.22896 3.45827C5.39869 3.45827 5.54485 3.49599 5.66743 3.57143C5.79002 3.64451 5.88549 3.74587 5.95385 3.87553C6.02458 4.00519 6.06465 4.15252 6.07408 4.31754H7.18087C7.18087 4.29632 7.18087 4.27511 7.18087 4.25389C7.18087 4.23032 7.18087 4.20674 7.18087 4.18317C7.17615 3.92857 7.12311 3.69873 7.02175 3.49364C6.92274 3.28619 6.78601 3.1082 6.61156 2.95969C6.43712 2.81117 6.23202 2.69802 5.99629 2.62023C5.76055 2.54008 5.50242 2.5 5.22189 2.5C4.77399 2.5 4.39327 2.59076 4.07974 2.77228C3.76856 2.9538 3.53165 3.21193 3.36899 3.54668C3.20633 3.87907 3.125 4.27511 3.125 4.73479V5.27228C3.125 5.72961 3.20515 6.12447 3.36545 6.45686C3.52811 6.78925 3.76503 7.0462 4.0762 7.22772C4.38973 7.40924 4.77163 7.5 5.22189 7.5C5.59435 7.5 5.92674 7.43164 6.21906 7.29491C6.51137 7.15582 6.7424 6.96252 6.91213 6.71499C7.08422 6.46511 7.1738 6.17515 7.18087 5.84512C7.18087 5.8239 7.18087 5.80151 7.18087 5.77793C7.18087 5.75436 7.18087 5.73079 7.18087 5.70721H6.07408C6.06465 5.86752 6.02575 6.01014 5.95739 6.13508C5.88903 6.26002 5.79237 6.35903 5.66743 6.43211C5.54485 6.50283 5.39869 6.53819 5.22896 6.53819C5.03566 6.53819 4.86946 6.48986 4.73037 6.39321C4.59365 6.29656 4.48874 6.15394 4.41566 5.96535C4.34494 5.77676 4.30958 5.54691 4.30958 5.27581V4.73479Z"
              fill="white"
            />
          </svg>
          <span class="user_menu__coin_amount">{{ formattedCoin }}</span>
        </div>
      </div>
      <div class="user_menu__avatar_area">
        <img class="user_menu__avatar" :src="iconUrl" alt="" />
        <svg
          class="user_menu__arrow"
          :class="{ 'user_menu__arrow--open': isOpen }"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M20.5303 8.46967C20.8232 8.76256 20.8232 9.23744 20.5303 9.53033L12.5303 17.5303C12.2374 17.8232 11.7626 17.8232 11.4697 17.5303L3.46967 9.53033C3.17677 9.23744 3.17677 8.76256 3.46967 8.46967C3.76256 8.17678 4.23744 8.17678 4.53033 8.46967L12 15.9393L19.4697 8.46967C19.7626 8.17678 20.2374 8.17678 20.5303 8.46967Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </div>

    <transition name="user_menu__pop">
      <div v-if="isOpen" class="user_menu__dropdown" role="menu">
        <template v-for="(group, index) in menuGroups">
          <div
            v-if="index > 0"
            :key="`${group.key}__separator`"
            class="user_menu__separator"
            role="separator"
          />
          <div :key="group.key" class="user_menu__group" role="group">
            <p class="user_menu__group_label">{{ group.label }}</p>
            <template v-for="item in group.items">
              <a
                v-if="item.href"
                :key="item.key"
                :href="item.href"
                class="user_menu__item"
                role="menuitem"
                @click="close"
              >
                <span class="user_menu__item_label">{{ item.label }}</span>
                <ChevronRightIcon class="user_menu__item_arrow" />
              </a>
              <router-link
                v-else-if="item.to"
                :key="item.key"
                :to="item.to"
                class="user_menu__item"
                role="menuitem"
                @click.native="close"
              >
                <span class="user_menu__item_label">{{ item.label }}</span>
                <ChevronRightIcon class="user_menu__item_arrow" />
              </router-link>
            </template>
          </div>
        </template>

        <div class="user_menu__separator" role="separator" />
        <router-link
          :to="{ name: 'logout' }"
          class="user_menu__item user_menu__item--caution"
          role="menuitem"
          @click.native="close"
        >
          {{ $t('mypageLogout') }}
        </router-link>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import { RawLocation } from 'vue-router'
import Formatter from '@/utils/Formatter'
import ChevronRightIcon from '@/components/ChevronRightIcon.vue'

interface MenuItem {
  key: string
  label: string
  // to: SPA内遷移、href: アプリ外（/r/ 配下の別アプリなど）への遷移
  to?: RawLocation
  href?: string
}

interface MenuGroup {
  key: string
  label: string
  items: MenuItem[]
}

// 表示条件付きの定義用（最後に visible で filter して MenuGroup に変換する）
interface MenuItemDef extends MenuItem {
  visible: boolean
}

interface MenuGroupDef {
  key: string
  label: string
  visible: boolean
  items: MenuItemDef[]
}

@Component({
  components: { ChevronRightIcon },
})
export default class UserMenu extends Vue {
  @Prop({ default: '' })
  username!: string

  @Prop({ default: 0 })
  coin!: number

  @Prop({ default: '' })
  iconUrl!: string

  // 送金NG地域（LIVEポイント交換・支払い情報登録の出し分けに使用）
  @Prop({ default: false })
  isTransferNgRegion!: boolean

  // Live2Dモデルアップロード機能の利用可否
  @Prop({ default: false })
  canUseLive2d!: boolean

  isOpen = false

  get formattedCoin(): string {
    return Formatter.numberWithCommas(this.coin)
  }

  get menuGroups(): MenuGroup[] {
    // メニューの全体構造を宣言的に定義し、表示条件は visible で表現する
    const groupDefs: MenuGroupDef[] = [
      {
        key: 'account',
        label: this.$t('common__account').toString(),
        visible: !this.isTransferNgRegion,
        items: [
          {
            key: 'payment_registration',
            label: this.$t('paymentRegistration').toString(),
            to: { name: 'payment_registration' },
            visible: true,
          },
        ],
      },
      {
        key: 'streaming',
        label: this.$t(
          'settings__section_title__streaming_settings'
        ).toString(),
        visible: true,
        items: [
          {
            key: 'obs_stream',
            label: this.$t('common__game_commentary').toString(),
            to: { name: 'obs_stream' },
            visible: true,
          },
          {
            key: 'live2d_model_upload',
            label: this.$t('live2DModelUpload').toString(),
            to: { name: 'live2d_model_upload' },
            visible: this.canUseLive2d,
          },
        ],
      },
      {
        key: 'music_rights',
        label: this.$t('musicRights').toString(),
        visible: true,
        items: [
          {
            key: 'music_rights',
            label: this.$t('musicRights').toString(),
            to: { name: 'mypage_music_rights' },
            visible: true,
          },
        ],
      },
      {
        key: 'other',
        label: this.$t('common__other').toString(),
        visible: true,
        items: [
          {
            key: 'photo_album',
            label: this.$t('photo_album__title__photo_album').toString(),
            href: '/r/photo_albums',
            visible: true,
          },
        ],
      },
    ]

    // 表示対象のグループ・要素だけを残す（要素が空になったグループも除外）
    return groupDefs
      .filter((group) => group.visible)
      .map((group) => ({
        key: group.key,
        label: group.label,
        items: group.items
          .filter((item) => item.visible)
          .map(({ visible: _visible, ...item }) => item),
      }))
      .filter((group) => group.items.length > 0)
  }

  mounted(): void {
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('keydown', this.onKeydown)
  }

  beforeDestroy(): void {
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onKeydown)
  }

  toggle(): void {
    this.isOpen = !this.isOpen
  }

  close(): void {
    this.isOpen = false
  }

  private onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return
    }
    const root = this.$refs.root as HTMLElement | undefined
    if (root && !root.contains(event.target as Node)) {
      this.close()
    }
  }

  private onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close()
    }
  }
}
</script>

<style scoped>
.user_menu {
  position: relative;
}

.user_menu__trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
}

.user_menu__trigger:hover,
.user_menu__trigger--open {
  background-color: #f2f2f2;
}

.user_menu__trigger:active {
  background-color: #e8e8e8;
}

.user_menu__info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.user_menu__username {
  margin: 0;
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  font-style: normal;
  font-weight: 700;
  font-size: 10px;
  line-height: 16px;
  color: #0f0f0f;
}

.user_menu__coin {
  display: flex;
  align-items: center;
  gap: 4px;
}

.user_menu__coin_icon {
  display: block;
  width: 10px;
  height: 10px;
}

.user_menu__coin_amount {
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 16px;
  color: #737373;
}

.user_menu__avatar_area {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user_menu__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background-color: #f2f2f2;
}

.user_menu__arrow {
  display: block;
  width: 12px;
  height: 12px;
  color: #737373;
  transition: transform 0.2s ease;
}

.user_menu__arrow--open {
  transform: rotate(180deg);
}

.user_menu__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 1000;

  min-width: 295px;
  padding: 24px 8px;
  background-color: #ffffff;
  border-radius: 16px;
  border: 1px solid #e8e8e8;
}

.user_menu__group {
  display: flex;
  flex-direction: column;
}

.user_menu__group_label {
  margin: 0;
  padding: 16px 8px;
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #bababa;
}

.user_menu__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 8px;
  border-radius: 6px;

  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #0f0f0f;
  text-decoration: none;
  cursor: pointer;
}

.user_menu__item:hover {
  background-color: #f2f2f2;
}

.user_menu__item--caution {
  min-height: 56px;
  color: #ff003d;
}

.user_menu__item_label {
  flex: 1;
}

.user_menu__item_arrow {
  position: absolute;
  top: 50%;
  right: 8px;
  width: 16px;
  height: 16px;
  transform: translateY(-50%);
  color: #737373;
}

.user_menu__separator {
  height: 1px;
  margin: 12px -8px;
  background-color: #e8e8e8;
}

.user_menu__pop-enter-active,
.user_menu__pop-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}

.user_menu__pop-enter,
.user_menu__pop-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
  transform-origin: top right;
}
</style>
